import { useLoaderData, useNavigate } from "react-router-dom";
import { useState, useLayoutEffect } from "react";
import { arrayRemove, deleteDoc, doc, updateDoc, writeBatch } from "firebase/firestore";
import CupDisplay from "../../../mk8dx/components/track-components/CupDisplay";
import WorldCup from "../../../world/components/Cup";
import ChangeUsername from "../../components/ChangeUsername/ChangeUsername";
import Button from "../../components/Button/Button";
import { deleteUser } from "firebase/auth";
import DeleteAccount from "../../components/pops/DeleteAccount/DeleteAccount";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import FullScreenPop from "../../components/pops/FullScreenPop";
import trackToString from "../../../mk8dx/functions/trackToString";
import worldTrackToString from "../../../world/functions/trackToString";
import SmallPop from "../../components/pops/SmallPop";
import { useFirebase } from "../../../contexts/FirebaseContext";
import "./Profile.css";

import { ReactComponent as ClearIcon } from "../../../assets/icons/svg/clear-inverse-svgrepo-com.svg";
import { ReactComponent as UndoIcon } from "../../../assets/icons/svg/undo-left-round-svgrepo-com.svg";
import { ReactComponent as PenIcon } from "../../../assets/icons/svg/pen-svgrepo-com.svg";
import { ReactComponent as DangerIcon } from "../../../assets/icons/svg/danger-svgrepo-com.svg";

export default function ProfileEdit() {
    const loader = useLoaderData();
    const navigate = useNavigate();
    const { auth, firestore } = useFirebase();

    const [displayName, setDisplayName] = useState(loader.displayName);
    const [showReauth, setShowReauth] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [deleteCup, setDeleteCup] = useState(null);
    const [editCup, setEditCup] = useState(null);
    const [editedCupName, setEditedCupName] = useState("");

    // navigate out if no access to page
    useLayoutEffect(() => {
        if (!loader?.displayName || loader?.id !== auth?.currentUser?.uid) {
            navigate(`/profile/${loader?.displayName}`, { replace: true });
        }
    }, [loader, navigate, auth]);

    const [smallPop, setSmallPop] = useState();
    useLayoutEffect(() => {
        const timeout = setTimeout(() => {
            setSmallPop(false);
        }, 3500);

        return () => clearTimeout(timeout);
    }, [smallPop]);

    const changeName = async () => {
        const userDoc = doc(firestore, "users", auth.currentUser.uid);
        await updateDoc(userDoc, {
            displayName: displayName,
            displayNameLower: displayName.toLowerCase()
        })
            .catch((e) => setSmallPop(e.message));
    }

    return <div id="main-container">
        <div>
            <Button
                text="Return to profile"
                icon={UndoIcon}
                onClick={() => navigate(`/profile/${loader.displayName}`)}
            />

            <ChangeUsername
                displayName={loader.displayName}
                displayNameLower={loader.displayNameLower}
                changeName={setDisplayName}
                onSubmit={changeName}
                uid={auth.currentUser?.uid}
                showChangeButton={true}
            />

            <div id="pfp-user-cups">
                <h2>Manage Your Cups:</h2>
                
                {loader.worldCups?.length > 0 && (
                    <>
                        <h3>Mario Kart World Cups:</h3>
                        <div id="cup-grid">
                            {
                                loader.worldCups.map((element) => {
                                    return <div key={element.id} className="cup-card cup-mini">
                                        <div className="cup-edit-header">
                                            <h3 className="cup-title">{element.cupName || "Unnamed World Cup"}</h3>
                                            <Button
                                                icon={PenIcon}
                                                text="Edit"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setEditCup({ ...element, type: 'world' });
                                                    setEditedCupName(element.cupName);
                                                }}
                                                className="icon-button"
                                            />
                                        </div>
                                        <WorldCup tracks={element.tracks} />
                                        <div className="cup-delete-section">
                                            <Button
                                                icon={ClearIcon}
                                                text="Delete Cup"
                                                color="red"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setDeleteCup({ ...element, type: 'world' });
                                                }}
                                                className="icon-button"
                                            />
                                        </div>
                                    </div>
                                })
                            }
                        </div>
                    </>
                )}
                
                {loader.cups?.length > 0 && (
                    <>
                        <h3>Mario Kart 8 Deluxe Cups:</h3>
                        <div id="cup-grid">
                            {
                                loader.cups.map((element) => {
                                    return <div key={element.id} className="cup-card">
                                        <div className="cup-edit-header">
                                            <h3 className="cup-title mk8dx-font">{element.cupName || "Unnamed MK8DX Cup"}</h3>
                                            <Button
                                                icon={PenIcon}
                                                text="Edit"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setEditCup({ ...element, type: 'mk8dx' });
                                                    setEditedCupName(element.cupName);
                                                }}
                                                className="icon-button"
                                            />
                                        </div>
                                        <CupDisplay
                                            tracks={element.tracks}
                                            variant="browse"
                                            showEmblems={true}
                                        />
                                        <div className="cup-delete-section">
                                            <Button
                                                icon={ClearIcon}
                                                text="Delete Cup"
                                                color="red"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setDeleteCup({ ...element, type: 'mk8dx' });
                                                }}
                                                className="icon-button"
                                            />
                                        </div>
                                    </div>
                                })
                            }
                        </div>
                    </>
                )}
            </div>



            {
                // pop handles delete
                deleteCup && <FullScreenPop
                    header={<> <DangerIcon /> Delete Cup </>}
                    closePop={() => setDeleteCup(null)}
                    component={<>
                        <p>Deleting a cup cannot be undone. Are you sure you want to delete this cup?</p>
                        <p>{deleteCup.cupName}</p>
                        <ol>
                            {
                                deleteCup.tracks.map((element, index) => {
                                    const trackString = deleteCup.type === 'world' 
                                        ? worldTrackToString(element)
                                        : trackToString(element);
                                    return <li key={index}>
                                        {trackString}
                                    </li>
                                })
                            }
                        </ol>
                        <Button
                            text="Delete Cup"
                            icon={ClearIcon}
                            color="red"
                            onClick={async () => {
                                const collection = deleteCup.type === 'world' ? 'worldCups' : 'cups';
                                const cupDoc = doc(firestore, collection, deleteCup.id);
                                const userDoc = doc(firestore, "users", auth.currentUser.uid);
                                const userField = deleteCup.type === 'world' ? 'worldCups' : 'cups';

                                await updateDoc(userDoc, {
                                    [userField]: arrayRemove(cupDoc)
                                })
                                    .then(async () => {
                                        await deleteDoc(cupDoc)
                                            .then(() => {
                                                setDeleteCup(null);
                                            });
                                    })
                                    .catch((e) => setSmallPop(e.message));
                                navigate(0, { replace: true });
                            }}
                        />
                    </>}
                />
            }

            {
                // pop handles cup name edit
                editCup && <FullScreenPop
                    header={<> <PenIcon /> Edit Cup Name </>}
                    closePop={() => setEditCup(null)}
                    component={<>
                        <p>Enter a new name for your cup:</p>
                        <input
                            type="text"
                            value={editedCupName}
                            onChange={(e) => setEditedCupName(e.target.value)}
                            maxLength={50}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                fontSize: '1rem',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                marginBottom: '1rem'
                            }}
                        />
                        <Button
                            text="Save Name"
                            icon={PenIcon}
                            color="green"
                            onClick={async () => {
                                if (!editedCupName.trim()) {
                                    setSmallPop("Cup name cannot be empty!");
                                    return;
                                }
                                
                                const collection = editCup.type === 'world' ? 'worldCups' : 'cups';
                                const cupDoc = doc(firestore, collection, editCup.id);

                                await updateDoc(cupDoc, {
                                    cupName: editedCupName.trim()
                                })
                                    .then(() => {
                                        setEditCup(null);
                                        setSmallPop("Cup name updated!");
                                        navigate(0, { replace: true });
                                    })
                                    .catch((e) => setSmallPop(e.message));
                            }}
                        />
                    </>}
                />
            }

            {
                // pop handles deleting account
                showReauth && <DeleteAccount
                    errorMessage={errorMessage}
                    close={() => setShowReauth(false)}
                    delete={async (password) => {
                        setErrorMessage("");
                        const credential = EmailAuthProvider.credential(
                            auth.currentUser.email, password
                        );
                        await reauthenticateWithCredential(auth.currentUser, credential)
                            .then(async () => {
                                // Delete all cups in a batch operation
                                if (loader.cups.length > 0 || loader.worldCups?.length > 0) {
                                    const batch = writeBatch(firestore);
                                    
                                    // Add all MK8DX cup deletions to batch
                                    loader.cups.forEach(cup => {
                                        const cupDoc = doc(firestore, "cups", cup.id);
                                        batch.delete(cupDoc);
                                    });
                                    
                                    // Add all World cup deletions to batch
                                    loader.worldCups?.forEach(cup => {
                                        const cupDoc = doc(firestore, "worldCups", cup.id);
                                        batch.delete(cupDoc);
                                    });
                                    
                                    // Execute all deletions at once
                                    await batch.commit();
                                }
                                
                                await deleteDoc(doc(firestore, "users", auth.currentUser.uid))
                                    .then(async () => {
                                        await deleteUser(auth.currentUser)
                                            .then(() => {
                                                navigate("/", { replace: true, state: { deletingAccount: true } });
                                            })
                                    })

                            }).catch((e) => {
                                setErrorMessage(e.message);
                                console.log(e.message);
                            });
                    }}
                />
            }

            <div className="danger-zone">
                <h2><DangerIcon /> Danger Zone <DangerIcon /></h2>
                <p>Deleting your account is permanent and cannot be undone. All your cups will be permanently removed.</p>
                <Button
                    text="Delete Account"
                    icon={ClearIcon}
                    color="red"
                    onClick={(e) => {
                        e.preventDefault();
                        setShowReauth(true);
                    }}
                />
            </div>

            {
                !!smallPop && <SmallPop text={smallPop} onClose={() => setSmallPop(null)} />
            }
        </div>
    </div>
}